// @ts-check

const PRODUCT_HISTORY_KEY = 'productSectionId';
const PRODUCT_SWAP_MEDIA_CLASS = 'product__media-wrapper--swapped';

/** @type {AbortController | null} */
let pendingProductRequest = null;

const POINTER_RELEASE_TIMEOUT_MS = 1000;
let isPointerPressed = false;
/** @type {Array<() => void>} */
let pointerReleaseWaiters = [];

const releasePointer = () => {
  isPointerPressed = false;
  const waiters = pointerReleaseWaiters;
  pointerReleaseWaiters = [];
  setTimeout(() => waiters.forEach((resolve) => resolve()), 0);
};

/**
 * Swapping the section while a pointer is down replaces the element under it, so the click never
 * fires and that selection is lost. Resolves after the pointer's click has dispatched.
 * @returns {Promise<void>}
 */
const waitForPointerRelease = () => {
  if (!isPointerPressed) return Promise.resolve();

  return new Promise((resolve) => {
    pointerReleaseWaiters.push(resolve);
    setTimeout(resolve, POINTER_RELEASE_TIMEOUT_MS);
  });
};

/**
 * @param {string} productUrl
 * @returns {URL}
 */
const toAbsoluteUrl = (productUrl) => new URL(productUrl, window.location.href);

/**
 * @param {string} sectionId
 * @param {string} productUrl
 * @param {AbortSignal} signal
 * @returns {Promise<Document>}
 */
const fetchProductSection = async (sectionId, productUrl, signal) => {
  const url = toAbsoluteUrl(productUrl);
  url.searchParams.set('section_id', sectionId);

  const response = await fetch(url, {
    headers: { 'X-Requested-With': 'XMLHttpRequest' },
    signal,
  });

  if (!response.ok) throw new Error(`Product section request failed with status ${response.status}`);

  return new DOMParser().parseFromString(await response.text(), 'text/html');
};

/**
 * The gallery renders the selected variant's featured media first, so a new id means a new image.
 * @param {Element} section
 */
const leadMediaId = (section) => section.querySelector('.product__media-list > [data-media-id]')?.getAttribute('data-media-id');

/**
 * @param {ParentNode} root
 * @returns {string[]}
 */
const stylesheetHrefs = (root) => Array.from(root.querySelectorAll('link[rel="stylesheet"][href]'), (link) => link.getAttribute('href') ?? '');

/**
 * @param {string} href
 * @returns {Promise<unknown>}
 */
const appendStylesheet = (href) => new Promise((resolve) => {
  const stylesheet = document.createElement('link');
  stylesheet.rel = 'stylesheet';
  stylesheet.href = href;
  stylesheet.addEventListener('load', resolve, { once: true });
  stylesheet.addEventListener('error', resolve, { once: true });
  document.head.append(stylesheet);
});

/** @type {Map<string, Promise<unknown>>} */
const appendedStylesheets = new Map();

/**
 * Partial swaps keep the page's original stylesheets, so a deploy since page load would leave new
 * markup unstyled. Load any stylesheet the response references that the page lacks.
 * @param {Document} html
 * @returns {Promise<unknown>}
 */
const loadMissingStylesheets = (html) => {
  const pageHrefs = new Set(stylesheetHrefs(document));
  const pending = stylesheetHrefs(html).flatMap((href) => {
    if (appendedStylesheets.has(href)) return [appendedStylesheets.get(href)];
    if (pageHrefs.has(href)) return [];
    const loading = appendStylesheet(href);
    appendedStylesheets.set(href, loading);
    return [loading];
  });
  return Promise.all(pending);
};

const REVIEW_SUMMARY_SELECTOR = '.junip-product-summary';
const REVIEW_SUMMARY_RENDERED_CLASS = 'junip-product-summary-rendered';

/**
 * Junip re-initializes every `.junip-product-summary` inserted into the DOM, which first paints the
 * bare Liquid placeholder. Keeping the initialized stars avoids that flash on each option change.
 * @param {HTMLElement} section
 * @returns {Array<{ blockId: string, summary: HTMLElement }>}
 */
const collectRenderedReviewSummaries = (section) =>
  Array.from(section.querySelectorAll(`${REVIEW_SUMMARY_SELECTOR}[data-initialized="true"]`), (summary) => ({
    blockId: summary.closest('.shopify-app-block')?.id ?? '',
    summary: /** @type {HTMLElement} */ (summary),
  })).filter(({ blockId }) => blockId);

/**
 * Same product: put the initialized element back so Junip leaves it alone. Linked product: carry the
 * rendered stars into the new placeholder so Junip updates the rating in place instead of re-mounting.
 * @param {HTMLElement} section
 * @param {Array<{ blockId: string, summary: HTMLElement }>} renderedSummaries
 */
const restoreRenderedReviewSummaries = (section, renderedSummaries) => {
  renderedSummaries.forEach(({ blockId, summary }) => {
    const placeholder = section.querySelector(`#${CSS.escape(blockId)} ${REVIEW_SUMMARY_SELECTOR}`);
    if (!(placeholder instanceof HTMLElement)) return;

    if (placeholder === summary) return;
    if (placeholder.dataset.productId === summary.dataset.productId) {
      placeholder.replaceWith(summary);
      return;
    }

    placeholder.innerHTML = summary.innerHTML;
    placeholder.classList.add(REVIEW_SUMMARY_RENDERED_CLASS);
  });
};

/**
 * @param {string} sectionId
 * @returns {{ [PRODUCT_HISTORY_KEY]: string }}
 */
const createProductHistoryState = (sectionId) => ({ [PRODUCT_HISTORY_KEY]: sectionId });

/** @param {string} sectionId */
const markCurrentHistoryEntry = (sectionId) => {
  if (window.history.state?.[PRODUCT_HISTORY_KEY]) return;

  window.history.replaceState(
    { ...window.history.state, ...createProductHistoryState(sectionId) },
    '',
    window.location.href
  );
};

/**
 * @param {string} sectionId
 * @param {string} productUrl
 */
const recordProductNavigation = (sectionId, productUrl) => {
  const targetUrl = toAbsoluteUrl(productUrl);
  const state = createProductHistoryState(sectionId);

  if (targetUrl.pathname === window.location.pathname) {
    window.history.replaceState(state, '', targetUrl);
    return;
  }

  markCurrentHistoryEntry(sectionId);
  window.history.pushState(state, '', targetUrl);
};

/** @param {PopStateEvent} event */
const handleProductHistoryNavigation = (event) => {
  const sectionId = event.state?.[PRODUCT_HISTORY_KEY];
  if (typeof sectionId !== 'string') return;

  const picker = document.querySelector(`#shopify-section-${sectionId} variant-picker`);

  if (!(picker instanceof VariantPicker)) {
    window.location.reload();
    return;
  }

  picker.showProduct(window.location.href);
};

class VariantPicker extends HTMLElement {
  constructor() {
    super();

    this.sectionId = '';
    /** @type {AbortController | null} */
    this.productRequest = null;
    this.isRenderingProduct = false;
    this.handleOptionChange = this.handleOptionChange.bind(this);
    this.handleSizeReselected = this.handleSizeReselected.bind(this);
  }

  connectedCallback() {
    this.sectionId = this.dataset.sectionId ?? '';
    this.addEventListener('change', this.handleOptionChange);
    this.addEventListener('size-reselected', this.handleSizeReselected);
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.handleOptionChange);
    this.removeEventListener('size-reselected', this.handleSizeReselected);
    // Rendering can replace this picker; only an unrelated removal cancels the swap.
    if (!this.isRenderingProduct) this.productRequest?.abort();
  }

  /** @param {Event} event */
  handleSizeReselected(event) {
    if (!this.classList.contains('variant-picker--playmat') || this.dataset.variantAvailable !== 'false') return;
    const input = event.target;
    if (input instanceof HTMLInputElement) this.settlePlaymatRows(input);
  }

  /** @param {Event} event */
  async handleOptionChange(event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.type !== 'radio' || input.hasAttribute('data-local-option')) return;

    const productElement = /** @type {HTMLElement | null} */ (this.closest('#product'));
    const productUrl = input.dataset.productUrl || productElement?.dataset.productUrl;
    if (!productUrl) return;

    const sectionId = this.sectionId;
    await this.showProduct(productUrl, () => {
      recordProductNavigation(sectionId, productUrl);
      const isPlaymat = this.classList.contains('variant-picker--playmat');
      const rendered = document.getElementById(input.id);
      const trigger = /** @type {HTMLButtonElement | null | undefined} */ (rendered?.closest('size-select')?.querySelector('[data-size-select-trigger]'));
      (trigger ?? rendered)?.focus({ preventScroll: isPlaymat });
      if (isPlaymat) this.settlePlaymatRows(input);
    });
  }

  /**
   * @param {string} productUrl
   * @param {() => void} [onShown]
   * @returns {Promise<boolean>}
   */
  async showProduct(productUrl, onShown) {
    if (!this.isConnected) return false;

    const sectionId = this.sectionId;
    const busyElement = document.querySelector(`#shopify-section-${sectionId} .product-info__main`);

    pendingProductRequest?.abort();
    const request = new AbortController();
    pendingProductRequest = request;
    this.productRequest = request;
    const isCurrentRequest = () => pendingProductRequest === request && !request.signal.aborted;
    const clearBusy = () => {
      if (pendingProductRequest !== request) return;
      pendingProductRequest = null;
      busyElement?.removeAttribute('aria-busy');
    };
    request.signal.addEventListener('abort', clearBusy, { once: true });
    busyElement?.setAttribute('aria-busy', 'true');

    try {
      const html = await fetchProductSection(sectionId, productUrl, request.signal);
      await Promise.all([loadMissingStylesheets(html), waitForPointerRelease()]);
      if (!isCurrentRequest() || !this.isConnected) return false;

      this.isRenderingProduct = true;
      try {
        this.renderProductSection(html, productUrl);
      } finally {
        this.isRenderingProduct = false;
      }

      onShown?.();
      return true;
    } catch (error) {
      if (!isCurrentRequest()) return false;

      console.error('Linked product swap failed, loading the page instead:', error);
      window.location.assign(productUrl);
      return false;
    } finally {
      clearBusy();
      if (this.productRequest === request) this.productRequest = null;
    }
  }

  /** @param {Document} html @param {string} [productUrl] */
  renderProductSection(html, productUrl) {
    const sectionId = this.sectionId;
    const section = document.getElementById(`shopify-section-${sectionId}`);
    const newSection = html.getElementById(`shopify-section-${sectionId}`);
    if (!section || !newSection) throw new Error(`Section ${sectionId} is missing from the response`);

    const newPageTitle = /** @type {HTMLElement | null} */ (html.querySelector('#product'))?.dataset
      .pageTitleTagInnerText;
    if (newPageTitle && newPageTitle !== document.title) document.title = newPageTitle;

    const newEmbroideryModals = html.querySelectorAll('modal-dialog[id*="EmbroideryPopup"]');

    const renderedSummaries = collectRenderedReviewSummaries(section);
    if (this.classList.contains('variant-picker--playmat')) {
      this.updateSectionPartially(section, newSection);
    } else {
      section.innerHTML = newSection.innerHTML;
    }
    restoreRenderedReviewSummaries(section, renderedSummaries);
    section.querySelector('.product__media-wrapper')?.classList.add(PRODUCT_SWAP_MEDIA_CLASS);

    this.refreshRecommendations(section);
    this.replaceEmbroideryModals(newEmbroideryModals);
    this.initializeCustomizationCheck();
    this.replaceModalSlider(html);

    document.dispatchEvent(new CustomEvent('variant-updated', { detail: { url: productUrl } }));
  }

  /** @param {HTMLInputElement} changedInput */
  settlePlaymatRows(changedInput) {
    const rowKey = changedInput.closest('[data-row-key]')?.getAttribute('data-row-key');
    const changedRow = document.querySelector(`.variant-picker--playmat details[data-row-key="${rowKey}"]`);
    if (changedRow instanceof HTMLDetailsElement && !changedRow.hasAttribute('data-keep-open')) changedRow.open = false;
    if (rowKey === 'size') {
      const designRow = document.querySelector('.variant-picker--playmat details[data-row-key="design"]');
      if (designRow instanceof HTMLDetailsElement) {
        designRow.open = designRow.closest('variant-picker')?.getAttribute('data-variant-available') !== 'false';
      }
    }
  }

  /** @param {HTMLElement} currentSection @param {HTMLElement} nextSection */
  updateSectionPartially(currentSection, nextSection) {
    const currentProduct = currentSection.querySelector('#product');
    const nextProduct = nextSection.querySelector('#product');
    if (!(currentProduct instanceof HTMLElement) || !(nextProduct instanceof HTMLElement)) throw new Error('Product container is missing');
    const productChanged = currentProduct.dataset.productId !== nextProduct.dataset.productId;

    if (productChanged || leadMediaId(currentSection) !== leadMediaId(nextSection)) {
      const currentMedia = currentSection.querySelector('.product__media-wrapper');
      const nextMedia = nextSection.querySelector('.product__media-wrapper');
      if (currentMedia && nextMedia) currentMedia.replaceWith(nextMedia);
    }

    const currentInfo = currentSection.querySelector('.product__info-container');
    const nextInfo = nextSection.querySelector('.product__info-container');
    if (!currentInfo || !nextInfo) throw new Error('Product information is missing');
    const currentBlocks = Array.from(currentInfo.children);
    const nextBlocks = Array.from(nextInfo.children);
    const openRows = Array.from(currentInfo.querySelectorAll('details[data-row-key][open]'), (row) => row.getAttribute('data-row-key'));
    nextInfo.querySelectorAll('details[data-row-key]').forEach((row) => row.toggleAttribute('open', openRows.includes(row.getAttribute('data-row-key'))));
    const designPickerState = currentInfo.querySelector('playmat-design-picker')?.state;
    nextBlocks.forEach((nextBlock, index) => this.replaceBlockIfChanged(currentBlocks[index], nextBlock, designPickerState));
    currentBlocks.slice(nextBlocks.length).forEach((extraBlock) => extraBlock.remove());

    Object.assign(currentProduct.dataset, nextProduct.dataset);
    if (!nextProduct.hasAttribute('data-native-bundles')) delete currentProduct.dataset.nativeBundles;
  }

  /** @param {Element | undefined} currentBlock @param {Element} nextBlock @param {PlaymatDesignPickerState | undefined} designPickerState */
  replaceBlockIfChanged(currentBlock, nextBlock, designPickerState) {
    if (!currentBlock) return document.querySelector('.product__info-container')?.appendChild(nextBlock);
    if (currentBlock.outerHTML === nextBlock.outerHTML) return;

    if (currentBlock.matches('details[open]')) nextBlock.setAttribute('open', '');
    currentBlock.replaceWith(nextBlock);
    nextBlock.querySelector('playmat-design-picker')?.applyState(designPickerState);
  }

  /** @param {HTMLElement} mainSection */
  refreshRecommendations(mainSection) {
    const newProductId = /** @type {HTMLElement | null} */ (document.querySelector('#product'))?.dataset.productId;
    if (!newProductId) return;

    document.querySelectorAll('product-recommendations').forEach((recommendations) => {
      if (mainSection.contains(recommendations) || recommendations.dataset.productId === newProductId) return;

      recommendations.dataset.productId = newProductId;
      recommendations.loadRecommendations(newProductId);
    });
  }

  /** @param {NodeListOf<Element>} newEmbroideryModals */
  replaceEmbroideryModals(newEmbroideryModals) {
    document.querySelectorAll('modal-dialog[id*="EmbroideryPopup"]').forEach((modal) => modal.remove());

    newEmbroideryModals.forEach((modal) => {
      const clonedModal = /** @type {ModalDialogElement} */ (modal.cloneNode(true));
      // Prevents ModalDialog.connectedCallback from moving the modal again
      clonedModal.moved = true;
      clonedModal.dataset.section = this.sectionId;
      document.body.appendChild(clonedModal);
    });
  }

  /** @param {Document} html */
  replaceModalSlider(html) {
    const sliderId = `#Slider-ProductMediaModal-${this.sectionId}`;
    const modalSlider = document.querySelector(sliderId);
    const newModalSlider = html.querySelector(sliderId);
    if (!modalSlider || !newModalSlider) return;

    modalSlider.innerHTML = newModalSlider.innerHTML;
    modalSlider.closest('slider-component')?.resetPages();
  }

  initializeCustomizationCheck() {
    // Wait for custom elements to be fully initialized
    setTimeout(() => {
      const productForm = document.querySelector('product-form');
      if (!productForm) return;

      if (typeof productForm.checkForCustomizedVariantOnLoad === 'function') {
        productForm.checkForCustomizedVariantOnLoad();
      } else {
        this.manualCustomizationCheck(productForm);
      }
    }, 600);
  }

  /** @param {HTMLElement} productForm */
  manualCustomizationCheck(productForm) {
    // Replicate the logic from product-form.js performCustomizationCheck
    const isCustomized = this.isCustomizedVariant();
    const submitButton = productForm.querySelector('[type="submit"]');
    const submitButtonText = submitButton?.querySelector('.product-form__add-button-text');

    if (isCustomized && !this.hasPersonalizationData()) {
      this.showCustomizationRequiredMessage(productForm);

      submitButton?.setAttribute('disabled', 'disabled');
      if (submitButtonText) submitButtonText.textContent = 'Personalize to Add to Bag';
    } else if (!isCustomized) {
      document.querySelector('.customization-required-message')?.remove();

      submitButton?.removeAttribute('disabled');
      if (submitButtonText) submitButtonText.textContent = window.variantStrings?.addToCart || 'Add to Bag';
    }
  }

  /** @returns {boolean} */
  hasPersonalizationData() {
    /** @param {string} name */
    const fieldValue = (name) =>
      /** @type {HTMLInputElement | null} */ (document.querySelector(`[name="properties[${name}]"]`))?.value?.trim();

    return Boolean(fieldValue('Embroidery Text') && fieldValue('Embroidery Font') && fieldValue('Embroidery Thread Color'));
  }

  /** @returns {boolean} */
  isCustomizedVariant() {
    const variantPicker = document.querySelector('variant-picker');
    const selectedOptions = variantPicker
      ? Array.from(variantPicker.querySelectorAll('input[type="radio"]:checked'), (input) => /** @type {HTMLInputElement} */ (input))
      : [];

    return (
      selectedOptions.some((input) => input.value === 'Customized') ||
      Boolean(document.querySelector('.embroidery-popup-modal__button'))
    );
  }

  /** @param {HTMLElement} productForm */
  showCustomizationRequiredMessage(productForm) {
    document.querySelector('.customization-required-message')?.remove();

    const messageEl = document.createElement('div');
    messageEl.className = 'customization-required-message alert alert--warning';
    messageEl.innerHTML = `
      <p class="body-xs">This variant requires personalization. Please click "Personalize it" to add your embroidery details before adding to cart.</p>
    `;

    const productFormContainer = productForm.closest('.product-form') || productForm.closest('.product__content');
    const form = productForm.querySelector('form');
    const targetParent = productFormContainer || (form ? form.parentElement : productForm);

    if (targetParent && form) {
      targetParent.insertBefore(messageEl, form);
      return;
    }

    const submitButton = productForm.querySelector('[type="submit"]');
    submitButton?.parentElement?.insertBefore(messageEl, submitButton);
  }
}

customElements.define('variant-picker', VariantPicker);
window.addEventListener('popstate', handleProductHistoryNavigation);
document.addEventListener('pointerdown', () => { isPointerPressed = true; }, true);
document.addEventListener('pointerup', releasePointer, true);
document.addEventListener('pointercancel', releasePointer, true);
